/**
 * The Scope of Work page — eight steps, in the order the crew works.
 *
 * It sits directly under the inspection photographs on the presented document
 * (Jonathon, 2026-08-16): the customer has just seen what is wrong with their
 * roof, and this is what we are going to do about it, before anybody is asked
 * to look at a price.
 *
 * **It is only true of a shingle tear-off.** "Remove your existing roof system
 * down to the decking" and "install your brand-new shingles" describe one kind
 * of job; on a TPO flat roof, a standing seam panel job or a repair they are a
 * promise nobody meant to make. `scopeOfWorkApplies` is the gate, and it is
 * deliberately closed by default — an estimate that cannot prove it is a
 * shingle replacement does not get the page.
 *
 * The words are the owner's, transcribed from the packet he wrote, and they are
 * frozen into each document at send like everything else on it (ADR 0032):
 * rewording a step here changes the next proposal, never one already in
 * somebody's hands. The photographs are not frozen — `image` is a key, and the
 * renderer owns where that file lives.
 */

/** One step of the build, as the customer reads it. */
export interface ScopeStep {
  /** Which photograph. A key, resolved to a file by whatever draws this. */
  image: string;
  heading: string;
  /** What that step means for them. Never a quantity, never a price. */
  points: string[];
}

/** The banner under the last step. */
export interface ScopeCloser {
  /** The bar across the top of it. */
  bar: string;
  /** The promise underneath. */
  line: string;
}

export const SCOPE_OF_WORK_STEPS: readonly ScopeStep[] = [
  {
    image: "tear-off",
    heading: "Tear off",
    points: [
      "Remove your existing roof system down to the decking",
      "Clear all protruding fasteners, old vents & metal flashings",
    ],
  },
  {
    image: "decking",
    heading: "Inspect the decking",
    points: [
      "Inspect every board of decking for damage",
      "Replace any compromised decking as needed",
    ],
  },
  {
    image: "drip-edge",
    heading: "Drip edge and underlayment",
    points: [
      "Drip edge installed over the fascia, to code",
      "Install HT or synthetic underlayment based on your package",
      "HT underlayment is what creates a fully waterproof membrane over every facet of the roof",
    ],
  },
  {
    image: "starter-strips",
    heading: "New starter strips",
    points: [
      "Locks your shingles down against high winds",
      "Seals out rain at the roof’s edges",
      "Covers the seams at the bottom of the shingle so water doesn’t seep in",
    ],
  },
  {
    image: "roof-material",
    heading: "New roof material",
    points: [
      "Install your brand-new shingles or TimberSteel",
      "Style & color — completely your choice",
    ],
  },
  {
    image: "ventilation",
    heading: "Attic ventilation",
    points: [
      "Prevents heat & moisture buildup in your attic",
      "Helps your roof last longer & lowers energy costs",
      "Protects your warranty — sized right for your home",
    ],
  },
  {
    image: "hip-and-ridge",
    heading: "Hip and ridge caps",
    points: ["Brand-new hip and ridge caps, color-matched to your roof"],
  },
  {
    image: "clean-up",
    heading: "Full clean-up",
    points: [
      "All debris cleaned & hauled away",
      "Magnetic roller swept for every stray nail",
    ],
  },
];

export const SCOPE_OF_WORK_CLOSER: ScopeCloser = {
  bar: "Top to bottom, 100% new",
  line: "Everything on your roof will be brand new",
};

/**
 * The roof types these eight steps describe.
 *
 * `Metal Shingle` is TimberSteel, and it is on this list on purpose: it is laid
 * like a shingle roof over a full tear-off, and step 5 has named it since the
 * packet was written (confirmed by Jonathon, 2026-08-16). Standing seam, AG and
 * R panel, and `Flat / Low Slope` — the TPO roofs — are not on it, because
 * their sequence is a different sequence.
 *
 * Matched case-insensitively on the trimmed value: a roof type is free text on
 * an estimate, copied from the template that built it (rule 8).
 */
const SCOPE_ROOF_TYPES: readonly string[] = ["shingle", "metal shingle"];

/** What deciding whether to show the page depends on. */
export interface QuotedRoof {
  /** `estimates.roof_type` — "Shingle", "Standing Seam Metal", "Flat / Low Slope". */
  roofType?: string | null | undefined;
  /**
   * `estimates.trade` — "Roofing Replacement", "Roofing Repairs",
   * "Roofing New Construction".
   */
  trade?: string | null | undefined;
}

/**
 * Trades whose roof has nothing on it to take off.
 *
 * Matched on a word, like "repair" below, because a trade is free text and a
 * new one is added rather than renamed (rule 8). A new build is shingled over
 * a deck that is already covered: step 1 of the page, "remove your existing
 * roof system down to the decking", describes work nobody is going to do, and
 * the decking and underlayment steps after it are somebody else's (Jonathon,
 * 2026-09-28: the new construction template "won't have the graphic with the
 * steps or any verbiage about tear off"). ADR 0223.
 */
const NOTHING_TO_TEAR_OFF: readonly string[] = ["new construction"];

const normalise = (value: string | null | undefined): string =>
  (value ?? "").trim().toLowerCase();

/**
 * Whether one estimate is the full shingle tear-off this page describes.
 *
 * Both halves have to hold. The roof type says what is going on; the trade says
 * whether the old one is coming off. A repair carries a repair trade however it
 * is priced, and three shingles replaced on a Tuesday is not "remove your
 * existing roof system down to the decking".
 *
 * A blank roof type is a no. An estimate built from scratch rather than from a
 * roof template records nothing about what it is, and the safe reading of
 * nothing is not "shingle" (Jonathon, 2026-08-16).
 */
export function isFullShingleReplacement(roof: QuotedRoof): boolean {
  if (!SCOPE_ROOF_TYPES.includes(normalise(roof.roofType))) return false;
  const trade = normalise(roof.trade);
  if (NOTHING_TO_TEAR_OFF.some((word) => trade.includes(word))) return false;
  return !trade.includes("repair");
}

/**
 * Whether a document gets the page at all.
 *
 * **Every** roof it quotes has to be a shingle replacement, not merely one of
 * them. The page sits above the packages, so it speaks for all of them; a
 * packet offering three shingle options and a standing seam would otherwise
 * tell the customer their metal roof begins with a shingle tear-off.
 *
 * A document quoting nothing gets nothing.
 */
export function scopeOfWorkApplies(roofs: readonly QuotedRoof[]): boolean {
  return roofs.length > 0 && roofs.every(isFullShingleReplacement);
}
